import { useRef, useState } from 'react';
import type { BodyMapSelection, BodyMapView, FormAnswer, FormFieldDefinition } from '../../../types/forms';
import {
  BODY_MAP_NOTE_MAX,
  BODY_MAP_REGIONS,
  BODY_MAP_VIEW_LABELS,
  bodyMapAnswer,
  bodyMapConfig,
  bodyMapRegionLabel,
  isBodyMapSelected,
  toggleBodyMapRegion,
} from '../../../lib/bodyMap';
import { BODY_MAP_PATHS, BODY_MAP_VIEWBOX } from './bodyMapArtwork';

interface BodyMapFieldProps {
  field: FormFieldDefinition;
  value: FormAnswer | undefined;
  onChange: (answer: FormAnswer) => void;
  error?: string;
}

// Site colors (src/hooks/useSiteColors.ts). Region outlines use the muted text
// color so every region stays visible (WCAG 1.4.11); selection adds a fill and
// a darker outline, so it doesn't rely on color alone.
const PRIMARY = 'var(--site-primary)';
const ON_PRIMARY = 'var(--site-on-primary)';
const CONTROL = 'var(--site-text-muted)';
const TEXT = 'var(--site-text)';
const REGION_FILL = 'var(--site-border)';
const FOCUS = 'var(--site-link)';

const regionName = (s: Pick<BodyMapSelection, 'view' | 'regionId'>) =>
  `${bodyMapRegionLabel(s.regionId)}, ${s.view}`;

function Diagram({
  view,
  selections,
  single,
  onToggle,
}: {
  view: BodyMapView;
  selections: BodyMapSelection[];
  single: boolean;
  onToggle: (regionId: string) => void;
}) {
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const regions = Object.entries(BODY_MAP_PATHS[view]);
  // The client's right is on the screen's left on the front view only. The
  // diagram never mirrors, so this row stays left-to-right in RTL pages too.
  const [screenLeft, screenRight] = view === 'front' ? ['Your right', 'Your left'] : ['Your left', 'Your right'];

  return (
    <div className="mx-auto w-full max-w-[240px]">
      <div className="flex justify-between text-xs text-site-muted mb-1" dir="ltr">
        <span>{screenLeft}</span>
        <span>{screenRight}</span>
      </div>
      <svg
        viewBox={`0 0 ${BODY_MAP_VIEWBOX.width} ${BODY_MAP_VIEWBOX.height}`}
        className="w-full h-auto touch-manipulation select-none"
        role={single ? 'radiogroup' : 'group'}
        aria-label={`${BODY_MAP_VIEW_LABELS[view]} of body`}
      >
        {regions.map(([id, d]) => {
          const selected = isBodyMapSelected(selections, view, id);
          return (
            <path
              key={id}
              d={d}
              role={single ? 'radio' : 'checkbox'}
              aria-checked={selected}
              aria-label={regionName({ view, regionId: id })}
              tabIndex={0}
              className="cursor-pointer outline-none"
              style={{ fill: selected ? PRIMARY : REGION_FILL, stroke: CONTROL, strokeWidth: 1 }}
              onClick={() => onToggle(id)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onToggle(id);
                }
              }}
              onFocus={e => setFocusedId(e.currentTarget.matches(':focus-visible') ? id : null)}
              onBlur={() => setFocusedId(null)}
            />
          );
        })}
        {/* Outlines drawn on top so neighbouring regions never cover them, while
            the tappable paths above keep a stable tab order. */}
        <g aria-hidden="true" pointerEvents="none" style={{ fill: 'none', strokeLinejoin: 'round' }}>
          {regions
            .filter(([id]) => isBodyMapSelected(selections, view, id))
            .map(([id, d]) => (
              <path key={id} d={d} style={{ stroke: TEXT, strokeWidth: 2.5 }} />
            ))}
          {focusedId && (
            <path d={BODY_MAP_PATHS[view][focusedId]} style={{ stroke: FOCUS, strokeWidth: 3.5 }} />
          )}
        </g>
      </svg>
    </div>
  );
}

function RegionList({
  view,
  selections,
  single,
  onToggle,
}: {
  view: BodyMapView;
  selections: BodyMapSelection[];
  single: boolean;
  onToggle: (regionId: string) => void;
}) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" role={single ? 'radiogroup' : 'group'} aria-label={`${BODY_MAP_VIEW_LABELS[view]} areas`}>
      {BODY_MAP_REGIONS[view].map(id => {
        const selected = isBodyMapSelected(selections, view, id);
        return (
          <button
            key={id}
            type="button"
            role={single ? 'radio' : 'checkbox'}
            aria-checked={selected}
            onClick={() => onToggle(id)}
            className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl border-2 transition-colors text-left ${
              selected ? 'border-current' : 'border-site-border'
            }`}
            style={selected ? { borderColor: PRIMARY } : undefined}
          >
            <span
              className={`w-5 h-5 border-2 flex items-center justify-center shrink-0 ${single ? 'rounded-full' : 'rounded-md'}`}
              style={{ borderColor: selected ? PRIMARY : CONTROL, backgroundColor: selected ? PRIMARY : 'transparent' }}
            >
              {selected && (
                <svg width="12" height="10" viewBox="0 0 12 10" fill="none" aria-hidden="true">
                  <path d="M1 5l3 3 7-7" style={{ stroke: ON_PRIMARY }} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </span>
            <span className="text-sm text-site-text">{bodyMapRegionLabel(id)}</span>
          </button>
        );
      })}
    </div>
  );
}

export function BodyMapField({ field, value, onChange, error }: BodyMapFieldProps) {
  const config = bodyMapConfig(field);
  // Kept locally so notes can be typed with trailing spaces; the answer gets trimmed notes.
  const [selections, setSelections] = useState<BodyMapSelection[]>(
    () => (value?.type === 'text' ? value.selections ?? [] : []),
  );
  const [view, setView] = useState<BodyMapView>(config.views[0]);
  const [asList, setAsList] = useState(false);
  // Latest selections, so taps that land before a re-render build on each other.
  const latest = useRef(selections);

  const update = (change: (current: BodyMapSelection[]) => BodyMapSelection[]) => {
    const next = change(latest.current);
    latest.current = next;
    setSelections(next);
    onChange(bodyMapAnswer(next, config.notes));
  };
  const toggle = (regionId: string) => update(cur => toggleBodyMapRegion(cur, view, regionId, config.single));
  const remove = (s: BodyMapSelection) => update(cur => toggleBodyMapRegion(cur, s.view, s.regionId, config.single));
  const setNote = (s: BodyMapSelection, notes: string) =>
    update(cur => cur.map(x => (x.view === s.view && x.regionId === s.regionId ? { ...x, notes } : x)));

  return (
    <div className="space-y-4">
      {config.views.length > 1 && (
        <div className="flex rounded-full border-2 border-site-border p-1" role="group" aria-label="Body view">
          {config.views.map(v => {
            const active = v === view;
            const count = selections.filter(s => s.view === v).length;
            return (
              <button
                key={v}
                type="button"
                aria-pressed={active}
                onClick={() => setView(v)}
                className={`flex-1 py-2 rounded-full text-sm font-medium transition-colors ${
                  active ? 'text-site-on-primary' : 'text-site-text'
                }`}
                style={active ? { backgroundColor: PRIMARY } : undefined}
              >
                {BODY_MAP_VIEW_LABELS[v]}
                {count > 0 && <span> · {count}</span>}
              </button>
            );
          })}
        </div>
      )}

      <div className={error ? 'rounded-xl border-2 border-site-danger/40 p-3' : ''}>
        {asList ? (
          <RegionList view={view} selections={selections} single={config.single} onToggle={toggle} />
        ) : (
          <Diagram view={view} selections={selections} single={config.single} onToggle={toggle} />
        )}
      </div>

      <button
        type="button"
        onClick={() => setAsList(!asList)}
        className="text-sm text-site-link underline underline-offset-2"
      >
        {asList ? 'Show the diagram' : 'Choose from a list instead'}
      </button>

      <div>
        <div className="text-sm font-medium text-site-text mb-2">Selected areas</div>
        {selections.length === 0 ? (
          <p className="text-sm text-site-muted">No areas marked yet</p>
        ) : (
          <ul className="space-y-2">
            {selections.map(s => (
              <li key={`${s.view}:${s.regionId}`} className="rounded-xl border-2 border-site-border px-4 py-2.5">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm text-site-text">
                    {bodyMapRegionLabel(s.regionId)} · {BODY_MAP_VIEW_LABELS[s.view]}
                  </span>
                  <button
                    type="button"
                    onClick={() => remove(s)}
                    aria-label={`Remove ${regionName(s)}`}
                    className="text-site-muted hover:text-site-text text-lg leading-none px-1"
                  >
                    ×
                  </button>
                </div>
                {config.notes && (
                  <input
                    type="text"
                    value={s.notes}
                    maxLength={BODY_MAP_NOTE_MAX}
                    onChange={e => setNote(s, e.target.value)}
                    placeholder="Add a note (optional)"
                    aria-label={`Note for ${regionName(s)}`}
                    className="mt-2 w-full px-3 py-2 rounded-lg border-2 border-site-border text-sm focus:outline-none"
                  />
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
